import type { CryptoCurrency, FiatCurrency } from '@0xtorch/core'
import type { AssetRecord } from '../types'

type Asset = CryptoCurrency | FiatCurrency

type OptionalAsset = Asset | undefined

export type GetAsset = (symbol: string) => OptionalAsset

export const createGetAsset = (params: {
  assets: AssetRecord
  symbolAssetMap: Record<string, string | undefined>
}): GetAsset => {
  const { assets, symbolAssetMap } = params
  const cache: Record<string, OptionalAsset> = {}

  return (symbol) => {
    const upperSymbol = symbol.toUpperCase()

    // cache があれば cache から返す
    const cachedAsset = cache[upperSymbol]
    if (cachedAsset !== undefined) {
      return cachedAsset
    }

    // symbolAssetMap に upperSymbol があれば、 assetKey から asset 取得
    // 取得できたら cache に保存して返す
    const assetByMap = getAssetBySymbolAssetMap({
      assets,
      symbolAssetMap,
      upperSymbol,
    })
    if (assetByMap) {
      cache[upperSymbol] = assetByMap
      return assetByMap
    }

    // symbol が一致する asset から fiat > 時価総額最大 の優先度で取得
    // cache に保存して返す
    const assetBySymbol = getAssetBySymbol({
      assets,
      upperSymbol,
    })
    cache[upperSymbol] = assetBySymbol
    return assetBySymbol
  }
}

const getAssetBySymbolAssetMap = (params: {
  assets: AssetRecord
  symbolAssetMap: Record<string, string | undefined>
  upperSymbol: string
}): OptionalAsset => {
  const { assets, symbolAssetMap, upperSymbol } = params
  const assetKey = symbolAssetMap[upperSymbol]
  if (!assetKey) {
    return undefined
  }
  const [type, id] = assetKey.split('/')

  switch (type) {
    case 'crypto': {
      return assets.crypto[id]
    }
    case 'fiat': {
      return assets.fiat[id]
    }
    default: {
      return undefined
    }
  }
}

const getAssetBySymbol = (params: {
  assets: AssetRecord
  upperSymbol: string
}): OptionalAsset => {
  const { assets, upperSymbol } = params

  const assetKeys = assets.symbol[upperSymbol]
  if (!assetKeys || assetKeys.length === 0) {
    return undefined
  }

  let asset: CryptoCurrency | undefined
  for (const assetKey of assetKeys) {
    const [type, id] = assetKey.split('/')

    switch (type) {
      // crypto がある場合、時価総額最大のものを選択
      case 'crypto': {
        const crypto = assets.crypto[id]
        if (!crypto) {
          break
        }
        if (
          !asset?.market?.marketCapUsd ||
          (crypto.market?.marketCapUsd ?? 0) > asset.market.marketCapUsd
        ) {
          asset = crypto
        }
        break
      }
      // fiat がある場合、優先的に返す
      case 'fiat': {
        const fiat = assets.fiat[id]
        if (fiat) {
          return fiat
        }
        break
      }
      default: {
        continue
      }
    }
  }

  return asset
}
